import { useEffect, useState } from 'react';
import { generateLegalDescription } from '../lib/legalDescription.js';
import { polishLegalDescription } from '../lib/claudeApi.js';
import { exportParcelPdf, getMapCanvas } from '../lib/pdfExport.js';

// Shows parcel attributes plus a two-stage legal description:
//   Stage 1: mechanical output from Phase 3 (instant once PLSS is fetched)
//   Stage 2: Claude-polished version (Phase 4, replaces primary display)
// Raw stays visible under a "Raw output" toggle so you can compare.
export default function ParcelPanel({ parcels }) {
  const first = parcels?.features?.[0];
  const [desc, setDesc] = useState({ placeholder: true, kind: 'none', text: '' });
  const [polished, setPolished] = useState(null);   // string | null
  const [polishing, setPolishing] = useState(false);
  const [polishErr, setPolishErr] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [copyStatus, setCopyStatus] = useState(null); // 'copied' | null

  useEffect(() => {
    let cancelled = false;
    setPolished(null);
    setPolishErr(null);
    if (!first) {
      setDesc({ placeholder: true, kind: 'none', text: '' });
      return;
    }
    setDesc({ placeholder: true, kind: 'loading', text: 'Computing legal description (checking PLSS alignment)...' });
    generateLegalDescription(first)
      .then(async (result) => {
        if (cancelled) return;
        setDesc(result);
        if (result.placeholder || !result.text || (result.kind !== 'aliquot' && result.kind !== 'metes')) return;
        // Stage 2: Claude polish
        setPolishing(true);
        try {
          const text = await polishLegalDescription({ kind: result.kind, rawText: result.text, parcel: first });
          if (!cancelled) setPolished(text);
        } catch (err) {
          if (!cancelled) setPolishErr(err.message || 'Polish failed');
        } finally {
          if (!cancelled) setPolishing(false);
        }
      })
      .catch((err) => {
        if (!cancelled) setDesc({ placeholder: true, kind: 'error', text: 'Error: ' + err.message });
      });
    return () => { cancelled = true; };
  }, [first]);

  if (!first) {
    return (
      <section className="parcel-panel">
        <h3>Parcel</h3>
        <p className="empty">Search for an address or draw a polygon to see parcel details.</p>
      </section>
    );
  }

  const props = first.properties || {};
  const fields = props.fields || props;
  const display = [
    ['Address', fields.address || fields.saddress || fields.situsaddress],
    ['Owner', fields.owner || fields.owner1 || fields.ownername],
    ['Parcel ID', fields.parcelnumb || fields.parcel_id || fields.apn],
    ['County', fields.scounty || fields.county],
    ['State', fields.state2 || fields.state],
    ['Acres', formatAcres(fields.ll_gisacre || fields.gisacre || fields.acres)],
    ['Legal (Regrid)', fields.legaldesc || fields.legal_desc],
  ].filter(([, v]) => v != null && v !== '');

  const kindLabel = desc.kind === 'aliquot' ? 'Legal description (aliquot)'
    : desc.kind === 'metes' ? 'Legal description (metes & bounds)'
    : desc.kind === 'loading' ? 'Computing...'
    : 'Legal description';

  const primaryText = polished || desc.text || '';
  const primaryIsPolished = !!polished;

  return (
    <section className="parcel-panel">
      <h3>Parcel</h3>
      {display.map(([k, v]) => (
        <div className="field" key={k}>
          <span className="k">{k}</span>
          <span className="v">{String(v)}</span>
        </div>
      ))}
      <h3 style={{ marginTop: 16 }}>{kindLabel}</h3>
      {polishing && (
        <div className="status info" style={{ marginBottom: 8 }}>
          Polishing with Claude...
        </div>
      )}
      {polishErr && (
        <div className="status error" style={{ marginBottom: 8 }}>
          Claude polish failed: {polishErr}. Showing raw output.
        </div>
      )}
      <div className={`legal-block ${desc.placeholder ? 'placeholder' : ''}`}>
        {primaryText}
      </div>
      {!desc.placeholder && primaryText && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(primaryText);
                setCopyStatus('copied');
                setTimeout(() => setCopyStatus(null), 2000);
              } catch (err) {
                setCopyStatus('error');
                setTimeout(() => setCopyStatus(null), 2000);
              }
            }}
            style={{
              padding: '6px 10px', fontSize: 12, background: 'white', color: '#111827',
              border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer',
            }}
          >
            Copy
          </button>
          <button
            type="button"
            onClick={() => downloadAsText(primaryText, buildFilename(fields))}
            style={{
              padding: '6px 10px', fontSize: 12, background: 'white', color: '#111827',
              border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer',
            }}
          >
            Download .txt
          </button>
          <button
            type="button"
            onClick={() => {
              try {
                exportParcelPdf({
                  parcel: first,
                  legalText: primaryText,
                  kind: desc.kind,
                  mapCanvas: getMapCanvas(),
                });
              } catch (err) {
                console.error('PDF export failed:', err);
                setCopyStatus('error');
                setTimeout(() => setCopyStatus(null), 2000);
              }
            }}
            style={{
              padding: '6px 10px', fontSize: 12, background: '#2563eb', color: 'white',
              border: 'none', borderRadius: 6, cursor: 'pointer',
            }}
          >
            Download PDF
          </button>
          {copyStatus === 'copied' && (
            <span style={{ fontSize: 12, color: '#059669' }}>Copied!</span>
          )}
          {copyStatus === 'error' && (
            <span style={{ fontSize: 12, color: '#991b1b' }}>Copy failed</span>
          )}
        </div>
      )}
      {primaryIsPolished && (
        <>
          <button
            type="button"
            onClick={() => setShowRaw((v) => !v)}
            style={{
              marginTop: 8, fontSize: 12, background: 'transparent', border: 'none',
              color: '#2563eb', cursor: 'pointer', padding: 0,
            }}
          >
            {showRaw ? 'Hide raw output' : 'Show raw mechanical output'}
          </button>
          {showRaw && (
            <div className="legal-block" style={{ marginTop: 6, opacity: 0.8 }}>
              {desc.text}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function formatAcres(v) {
  if (v == null) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toFixed(3);
}

function downloadAsText(text, filename) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'legal-description.txt';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function buildFilename(fields) {
  const parts = [];
  const id = fields?.parcelnumb || fields?.parcel_id || fields?.apn;
  if (id) parts.push(String(id).replace(/[^A-Za-z0-9._-]+/g, '_'));
  const addr = fields?.address || fields?.saddress || fields?.situsaddress;
  if (addr) parts.push(String(addr).slice(0, 40).replace(/[^A-Za-z0-9._-]+/g, '_'));
  const base = parts.join('_') || 'legal-description';
  return base + '.txt';
}

